import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { scrollToSection } from "@/lib/scrollToSection";

const CATERING_URL = "https://4sisterskitchen.com/";
const logoSrc = "/assets/logo/logo-168.png";
const logoPngSrcSet = "/assets/logo/logo-168.png 168w, /assets/logo/logo-336.png 336w";
const logoWebpSrcSet = "/assets/logo/logo-168.webp 168w, /assets/logo/logo-336.webp 336w";

export function Navigation() {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const tickingRef = useRef(false);

  const navItems = [
    { key: 'home', href: '#hero' },
    { key: 'services', href: '#services' },
    { key: 'portfolio', href: '#portfolio' },
    { key: 'team', href: '#team' },
    { key: 'catering', href: CATERING_URL, external: true },
    { key: 'contact', href: '#contact' }
  ];

  const navigateToSection = (href: string) => {
    scrollToSection(href);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const controlNavbar = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;

      window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;

        if (currentScrollY < 50) {
          setIsVisible(true);
        } else if (currentScrollY > lastScrollYRef.current) {
          setIsVisible(false);
          setIsMobileMenuOpen(false);
        } else {
          setIsVisible(true);
        }

        lastScrollYRef.current = currentScrollY;
        tickingRef.current = false;
      });
    };

    window.addEventListener('scroll', controlNavbar, { passive: true });
    return () => window.removeEventListener('scroll', controlNavbar);
  }, []);

  return (
    <nav className={`fixed top-0 w-full bg-gray-900/95 backdrop-blur-sm z-50 shadow-sm transition-transform duration-300 ${
      isVisible ? 'translate-y-0' : '-translate-y-full'
    }`} data-testid="navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <picture>
              <source srcSet={logoWebpSrcSet} sizes="144px" type="image/webp" />
              <img 
                src={logoSrc}
                srcSet={logoPngSrcSet}
                sizes="144px"
                alt="4Sisters Events" 
                className="h-12 w-auto" 
                width="144"
                height="48"
                decoding="async"
                data-testid="logo"
              />
            </picture>
          </div>
          
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-5 lg:space-x-8">
              {navItems.map((item) => (
                item.external ? (
                  <a
                    key={item.key}
                    href={item.href}
                    className="text-sm lg:text-base text-gray-300 hover:text-primary transition-colors duration-200"
                    data-testid={`nav-${item.key}`}
                  >
                    {t(`nav.${item.key}`)}
                  </a>
                ) : (
                  <button
                    key={item.key}
                    onClick={() => navigateToSection(item.href)}
                    className="text-sm lg:text-base text-gray-300 hover:text-primary transition-colors duration-200"
                    data-testid={`nav-${item.key}`}
                  >
                    {t(`nav.${item.key}`)}
                  </button>
                )
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            
            <button
              className="md:hidden"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              aria-expanded={isMobileMenuOpen}
              data-testid="mobile-menu-button"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-300" />
              ) : (
                <Menu className="w-6 h-6 text-gray-300" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden" data-testid="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navItems.map((item) => (
                item.external ? (
                  <a
                    key={item.key}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-3 py-2 text-gray-300 hover:text-primary transition-colors duration-200"
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    {t(`nav.${item.key}`)}
                  </a>
                ) : (
                  <button
                    key={item.key}
                    onClick={() => navigateToSection(item.href)}
                    className="block px-3 py-2 text-gray-300 hover:text-primary transition-colors duration-200"
                    data-testid={`mobile-nav-${item.key}`}
                  >
                    {t(`nav.${item.key}`)}
                  </button>
                )
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
